import React, { useEffect, useState } from 'react';
import { Languages, X } from 'lucide-react';
import { useZakatGlobal } from '../context/ZakatGlobalContext';

/**
 * LanguageNudgeBanner — Amanah by Zakat.International V2
 *
 * On first visit, if the browser preferred language differs from the
 * currently-selected UI language AND we ship a translation for it,
 * show a discreet gold banner offering to switch — one tap.
 *
 * "One-shot" contract:
 *  - Once dismissed OR accepted, we store the fact in localStorage
 *    (`zi_lang_nudge_seen = "1"`) and never show it again on this device.
 *  - We also skip the nudge if the user has already made an *explicit*
 *    language pick (`languageExplicit: true` in the locale lock),
 *    because they've told us what they want.
 *
 * Design:
 *  - Fixed to the bottom of the viewport on mobile so it doesn't
 *    displace hero content; centered pill on desktop.
 *  - Slide-in from below, subtle spring, auto-dismiss after 20 s so it
 *    never blocks the page.
 */
const STORAGE_KEY = 'zi_lang_nudge_seen';
const AUTO_DISMISS_MS = 20000;

// Map browser locale tags → our internal language codes.
// Handles common tag variants (en-US → en, zh-Hant → skip, prs-AF → prs).
const BROWSER_TO_APP = {
  ar: 'ar', ur: 'ur', fr: 'fr', ms: 'ms', tr: 'tr', ru: 'ru',
  hi: 'hi', bn: 'bn', ml: 'ml', id: 'id', fa: 'fa', prs: 'prs',
  sw: 'sw', uz: 'uz', en: 'en',
};

const detectBrowserLang = () => {
  if (typeof navigator === 'undefined') return null;
  const raw = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
  const primary = raw.split('-')[0];
  return BROWSER_TO_APP[primary] || null;
};

const wasNudgeSeen = () => {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
};

const markNudgeSeen = () => {
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore */ }
};

const hasExplicitLangChoice = () => {
  try {
    const raw = localStorage.getItem('zi-locale-v1');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.languageExplicit;
  } catch { return false; }
};

export const LanguageNudgeBanner = ({ testid = 'lang-nudge' }) => {
  const { selectedLanguage, updateLanguage, languages } = useZakatGlobal();
  const [visible, setVisible] = useState(false);
  const [suggested, setSuggested] = useState(null);

  useEffect(() => {
    if (wasNudgeSeen() || hasExplicitLangChoice()) return;
    const browserLang = detectBrowserLang();
    if (!browserLang) return;
    if (!selectedLanguage) return;
    if (browserLang === selectedLanguage.code) return;
    // Only nudge if we actually ship that language in the app.
    const shipped = languages.find((l) => l.code === browserLang);
    if (!shipped) return;
    setSuggested(shipped);
    // Small delay so the banner doesn't clash with the hero mount animation.
    const showTimer = setTimeout(() => setVisible(true), 900);
    const autoDismiss = setTimeout(() => {
      setVisible(false);
      markNudgeSeen();
    }, AUTO_DISMISS_MS + 900);
    return () => { clearTimeout(showTimer); clearTimeout(autoDismiss); };
  }, [selectedLanguage, languages]);

  if (!visible || !suggested) return null;

  const isRTL = suggested.dir === 'rtl';
  const accept = () => {
    updateLanguage(suggested.code);
    markNudgeSeen();
    setVisible(false);
  };
  const dismiss = () => {
    markNudgeSeen();
    setVisible(false);
  };

  // Copy is written *in the suggested language* so the switch feels
  // native — no user needs to translate "View this in Urdu?" from
  // English before they know what they're clicking.
  const PROMPTS = {
    ar: { title: 'شاهد هذا بالعربية؟', cta: 'تفعيل العربية', dismiss: 'إغلاق' },
    ur: { title: 'اسے اردو میں دیکھیں؟', cta: 'اردو فعال کریں', dismiss: 'بند کریں' },
    fr: { title: 'Voir ceci en français ?', cta: 'Activer le français', dismiss: 'Fermer' },
    ms: { title: 'Lihat ini dalam Bahasa Melayu?', cta: 'Guna Bahasa Melayu', dismiss: 'Tutup' },
    tr: { title: 'Bunu Türkçe görmek ister misiniz?', cta: 'Türkçe’ye geç', dismiss: 'Kapat' },
    ru: { title: 'Показать это по-русски?', cta: 'Включить русский', dismiss: 'Закрыть' },
    hi: { title: 'इसे हिंदी में देखें?', cta: 'हिंदी सक्रिय करें', dismiss: 'बंद' },
    bn: { title: 'এটি বাংলায় দেখবেন?', cta: 'বাংলা চালু করুন', dismiss: 'বন্ধ' },
    ml: { title: 'ഇത് മലയാളത്തിൽ കാണണോ?', cta: 'മലയാളം സജീവമാക്കുക', dismiss: 'അടയ്ക്കുക' },
    id: { title: 'Lihat ini dalam Bahasa Indonesia?', cta: 'Aktifkan Bahasa Indonesia', dismiss: 'Tutup' },
    fa: { title: 'این را به فارسی ببینید؟', cta: 'فعال کردن فارسی', dismiss: 'بستن' },
    prs: { title: 'این را به دری ببینید؟', cta: 'فعال کردن دری', dismiss: 'بستن' },
    sw: { title: 'Ona hii kwa Kiswahili?', cta: 'Washa Kiswahili', dismiss: 'Funga' },
    uz: { title: 'Buni o\u2018zbekcha ko\u2018rsatishimizni xohlaysizmi?', cta: 'O\u2018zbekchaga o\u2018tish', dismiss: 'Yopish' },
    en: { title: 'View this in English?', cta: 'Switch to English', dismiss: 'Close' },
  };
  const p = PROMPTS[suggested.code] || PROMPTS.en;

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid={testid}
      className="fixed z-40 left-1/2 -translate-x-1/2 bottom-4 sm:bottom-6 w-[calc(100%-24px)] sm:w-auto sm:max-w-md animate-in fade-in slide-in-from-bottom-5 duration-500"
      role="dialog"
      aria-live="polite"
    >
      <div
        className="flex items-center gap-2 sm:gap-3 pl-4 pr-2 py-2 rounded-full border border-gold-500/50 shadow-[0_10px_40px_-12px_rgba(13,45,34,0.55)]"
        style={{
          background:
            'radial-gradient(400px 200px at 0% 50%, rgba(201,164,75,0.20), transparent 55%),' +
            '#EDF5F1',
        }}
      >
        <span className="shrink-0 w-8 h-8 rounded-full bg-gold-500/15 ring-1 ring-gold-500/40 flex items-center justify-center">
          <Languages className="w-4 h-4 text-gold-400" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-[13.5px] sm:text-[14px] text-[#0D1520] font-medium truncate">
            {p.title}
          </p>
          <p className="text-[10.5px] font-mono uppercase tracking-widest text-gold-300/80">
            {suggested.nameNative || suggested.code.toUpperCase()}
          </p>
        </div>
        <button
          type="button"
          onClick={accept}
          data-testid={`${testid}-accept`}
          className="shrink-0 inline-flex items-center h-9 px-3.5 rounded-full bg-gold-500 hover:bg-gold-400 text-[#0D1520] text-[12.5px] font-semibold transition-colors"
        >
          {p.cta}
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid={`${testid}-dismiss`}
          aria-label={p.dismiss}
          className="shrink-0 w-8 h-8 rounded-full text-[#4A5568] hover:text-[#0D1520] hover:bg-[#EFE7D8] flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default LanguageNudgeBanner;
